import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  tap,
  switchMap,
  from,
  of,
} from 'rxjs';
import { environment } from '../../../../environments/environment.development';
import { ApiResponse } from '../../../core/models/api-response.models';
import { AuthResult } from '../../../core/models/auth.models';
import { AuthService } from '../../../core/services/auth.service';
import { ErrorHandlingService } from '../../../core/services/error-handling.service';

@Injectable({
  providedIn: 'root',
})
export class SupportLoginService {
  private readonly API_URL = `${environment.apiUrl}/auth`;
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly errorHandler = inject(ErrorHandlingService);
  private readonly ORIGINAL_ADMIN_KEY = 'original_admin_data';

  readonly supportUserData = new BehaviorSubject<AuthResult | null>(null);
  readonly supportUserData$ = this.supportUserData.asObservable();

  constructor() {
    const org = localStorage.getItem('Selected_Org_Data');
    const supportLoggedIn = localStorage.getItem('isSupportLogin');
    if (org && supportLoggedIn) {
      try {
        const decryptedOrg = this.auth.decryptData(org);
        if (decryptedOrg) {
          this.supportUserData.next(decryptedOrg);

          this.auth.userData.next(decryptedOrg);
        }
      } catch (error) {
        console.error('Error decrypting support user data:', error);
        localStorage.removeItem('Selected_Org_Data');
        localStorage.removeItem('isSupportLogin');
      }
    }
  }

  supportLogin(userId: number): Observable<ApiResponse<AuthResult>> {
    return this.http
      .get<
        ApiResponse<AuthResult>
      >(`${this.API_URL}/support-sign-in?userId=${userId}`, { withCredentials: true })
      .pipe(
        tap((response: ApiResponse<AuthResult>) => {
          if (response.result) {
            const currentUserData = localStorage.getItem('user_data');
            if (currentUserData) {
              localStorage.setItem(this.ORIGINAL_ADMIN_KEY, currentUserData);
            }

            this.supportUserData.next(response?.result);
            const isSupportLogin = this.auth.encryptData(true);
            localStorage.setItem('isSupportLogin', isSupportLogin);

            this.auth.userData.next(response.result);

            const encryptedUserData = this.auth.encryptData(response.result);
            localStorage.setItem('user_data', encryptedUserData);
            localStorage.setItem('Selected_Org_Data', encryptedUserData);

            this.auth.logoutLMS();
            this.auth.setRole(response.result?.roles || []);
          }
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  supportLogout() {
    return this.http
      .post<
        ApiResponse<AuthResult>
      >(`${this.API_URL}/support-sign-out`, {}, { withCredentials: true })
      .pipe(
        switchMap((response: ApiResponse<AuthResult>) => {
          this.supportUserData.next(null);
          localStorage.removeItem('isSupportLogin');
          localStorage.removeItem('Selected_Org_Data');
          this.auth.orgData.next(null);

          this.auth.logoutLMS();

          const originalAdminData = localStorage.getItem(
            this.ORIGINAL_ADMIN_KEY
          );
          if (originalAdminData) {
            try {
              const decryptedAdminData =
                this.auth.decryptData(originalAdminData);
              this.auth.userData.next(decryptedAdminData);
              localStorage.setItem('user_data', originalAdminData);
              localStorage.removeItem(this.ORIGINAL_ADMIN_KEY);
            } catch (error) {
              console.error('Error restoring original admin data:', error);
              if (response.result) {
                const { ...userDataWithoutToken } = response.result;
                this.auth.userData.next(userDataWithoutToken);
                const encryptedData =
                  this.auth.encryptData(userDataWithoutToken);
                localStorage.setItem('user_data', encryptedData);
              }
            }
          } else if (response.result) {
            const { ...userDataWithoutToken } = response.result;
            this.auth.userData.next(userDataWithoutToken);
            const encryptedData = this.auth.encryptData(userDataWithoutToken);
            localStorage.setItem('user_data', encryptedData);
          }

          return from(this.auth.setRole(['System Admin'])).pipe(
            map(() => {
              return response;
            }),
            catchError(error => {
              console.error('Error setting role:', error);
              return of(response);
            })
          );
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }
}
